import classNames from 'classnames/bind';
import React, { ForwardedRef, forwardRef, ReactElement } from 'react';
import Button, { ButtonMode } from '../Button';
import Icon, { ICONS } from '../Icon';
import Loader from '../Loader';
import Tooltip from '../Tooltip';
import colors from '../../scss/_colors-export.module.scss';
import DataLoadError from '../DataLoadError';

import styles from './Widget.module.scss';

const cx = classNames.bind(styles);

type HeaderLinkProps = {
  children: React.ReactNode;
};

type DragButtonProps = {
  setRef?: (node: HTMLElement | null) => void;
  listeners?: Record<string, Function>;
  isDisabled?: boolean;
  tooltipText: string;
};

export interface SortableWidgetProps {
  showDragButton?: boolean;
  dragButtonProps?: DragButtonProps;
  isDragged?: boolean;
}

export interface WidgetProps extends SortableWidgetProps {
  headerTitle: string | ReactElement;
  headerLink?: React.FunctionComponent<HeaderLinkProps>;
  collapsable?: boolean;
  firstLine?: ReactElement;
  secondLine?: ReactElement;
  wrapFirstLine?: boolean;
  wrapSecondLine?: boolean;
  hideFirstLineWhenLoading?: boolean;
  hideSecondLineWhenLoading?: boolean;
  children?: ReactElement;
  footer?: ReactElement | null;
  showFooterDivider?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetryAfterErrorClick?: () => void;
  fixedHeight?: boolean;
  dataTestId?: string;
  transparent?: boolean;
  classNames?: string;
  isExpanded?: boolean;
  setIsExpanded?: (value: boolean) => void;
}

const renderDragButton = (showDragButton: boolean, dragButtonProps?: DragButtonProps, isDragged?: boolean) => {
  if (!showDragButton) {
    return null;
  }

  const { setRef, listeners, isDisabled, tooltipText } = dragButtonProps || {};
  return (
    <Tooltip content={tooltipText} disabled={isDragged}>
      <div>
        <Button
          dataTestId="widget-drag-button"
          disabled={isDisabled}
          mode={ButtonMode.TERTIARY}
          className={cx('drag-button', { 'is-dragged': isDragged })}
          ref={setRef}
          {...listeners}
        >
          <Icon name={ICONS.Draggable} />
        </Button>
      </div>
    </Tooltip>
  );
};

const renderHeaderFirstLine = (
  headerTitle: string | ReactElement,
  showDragButton: boolean,
  isExpanded?: boolean,
  setIsExpanded?: (value: boolean) => void,
  collapsable?: boolean,
  firstLine?: ReactElement,
  wrapFirstLine?: boolean,
  hideFirstLineWhenLoading?: boolean,
  headerLink?: React.FunctionComponent<HeaderLinkProps>,
  isLoading?: boolean,
  dataTestId?: string,
  dragButtonProps?: DragButtonProps,
  isDragged?: boolean
) => {
  if (collapsable) {
    const label = isExpanded ? 'Collapse' : 'Expand';

    return (
      <div className={cx('header-line')}>
        {renderDragButton(showDragButton, dragButtonProps, isDragged)}
        <Button
          className={cx('header-line', 'collapsable', { wrap: wrapFirstLine })}
          aria-controls={dataTestId}
          aria-expanded={isExpanded}
          aria-label={label}
          onClick={() => setIsExpanded?.(!isExpanded)}
          type="button"
        >
          {renderHeaderTitle(headerTitle, headerLink, isDragged, dataTestId)}
          {(!isLoading || !hideFirstLineWhenLoading) && firstLine}
          <Tooltip
            key={label}
            trigger={
              <span className={cx('expand-collapse-toggle', { expanded: isExpanded })}>
                <Icon width={12} height={12} name={ICONS.CaretDown} color={colors.tertiary700} />
              </span>
            }
            content={label}
          />
        </Button>
      </div>
    );
  }

  return (
    <div className={cx('header-line', { wrap: wrapFirstLine })}>
      <div className={cx('header-line-container')}>
        {renderDragButton(showDragButton, dragButtonProps, isDragged)}
        {renderHeaderTitle(headerTitle, headerLink, isDragged, dataTestId)}
      </div>
      {(!isLoading || !hideFirstLineWhenLoading) && !isDragged && firstLine}
    </div>
  );
};

const renderHeaderTitle = (
  title: string | ReactElement,
  link?: React.FunctionComponent<HeaderLinkProps>,
  isDragged?: boolean,
  testId?: string
) => {
  if (link && !isDragged) {
    const Wrapper = link;
    return <Wrapper data-testid={headerTitleLinkTestId(testId)}>{renderTitleContent(title, true, testId)}</Wrapper>;
  }
  return renderTitleContent(title, false, testId);
};

const renderTitleContent = (title: string | ReactElement, isLink: boolean, testId?: string) => (
  <h2 className={cx('title')} data-testid={headerTitleTestId(testId)}>
    {title}
    {isLink && (
      <span className={cx('link-icon')} data-testid={headerTitleLinkIconTestId(testId)}>
        <Icon width={12} height={12} name={ICONS.ArrowRight} color={colors.primary800} />
      </span>
    )}
  </h2>
);

const headerTitleTestId = (testId?: string) => testId && `${testId}-header-title`;
const headerTitleLinkTestId = (testId?: string) => testId && `${testId}-header-title-link`;
const headerTitleLinkIconTestId = (testId?: string) => testId && `${testId}-header-title-link-icon`;

const renderHeaderSecondLine = (
  secondLine?: ReactElement,
  wrapSecondLine?: boolean,
  hideSecondLineWhenLoading?: boolean,
  isLoading?: boolean
) => {
  if ((!isLoading || !hideSecondLineWhenLoading) && secondLine) {
    return <div className={cx('header-line', { wrap: wrapSecondLine })}>{secondLine}</div>;
  }
  return undefined;
};

const renderContent = (
  children: ReactElement | undefined,
  isLoading: boolean,
  isError: boolean,
  onRetryAfterErrorClick?: () => void
) => {
  if (isLoading) {
    return (
      <div className={cx('state-wrapper')}>
        <Loader position="center" />
      </div>
    );
  }

  if (isError && onRetryAfterErrorClick) {
    return (
      <div className={cx('state-wrapper')}>
        <DataLoadError onRetryClick={onRetryAfterErrorClick} />
      </div>
    );
  }

  if (!children) {
    return null;
  }

  return <div className={cx('content')}>{children}</div>;
};

const renderFooter = (footer?: ReactElement | null, isLoading?: boolean, showFooterDivider?: boolean) =>
  footer && !isLoading ? <div className={cx('footer', { divider: showFooterDivider })}>{footer}</div> : null;

const Widget = (props: WidgetProps, ref: ForwardedRef<HTMLElement>): React.ReactElement => {
  const {
    classNames,
    firstLine,
    wrapFirstLine,
    hideFirstLineWhenLoading = true,
    headerTitle,
    headerLink,
    collapsable = false,
    secondLine,
    wrapSecondLine,
    hideSecondLineWhenLoading = true,
    children,
    footer,
    showFooterDivider,
    isLoading,
    isError,
    onRetryAfterErrorClick,
    fixedHeight,
    dataTestId,
    transparent = false,
    showDragButton = false,
    dragButtonProps,
    isDragged,
    isExpanded = true,
    setIsExpanded,
  } = props;

  const className = cx('body', classNames, {
    transparent,
    'is-expanded': (isLoading || isError) && !fixedHeight,
    'is-fixed': !!fixedHeight,
    'is-dragged': isDragged,
  });

  return (
    <section className={className} data-testid={dataTestId} id={dataTestId} ref={ref}>
      <header className={cx('header')}>
        {renderHeaderFirstLine(
          headerTitle,
          showDragButton,
          isExpanded,
          setIsExpanded,
          collapsable,
          firstLine,
          wrapFirstLine,
          hideFirstLineWhenLoading,
          headerLink,
          isLoading,
          dataTestId,
          dragButtonProps,
          isDragged
        )}
        {!isDragged && renderHeaderSecondLine(secondLine, wrapSecondLine, hideSecondLineWhenLoading, isLoading)}
      </header>
      {!isDragged && isExpanded && renderContent(children, !!isLoading, !!isError, onRetryAfterErrorClick)}
      {!isDragged && isExpanded && renderFooter(footer, isLoading, showFooterDivider)}
    </section>
  );
};

export default forwardRef(Widget);
